import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Collapse,
  Grid,
  Chip,
  Tooltip,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

import ReferenceImagePreviewList from "./ReferenceImagePreviewList";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailIcon from "@mui/icons-material/Email";
import CircularProgress from "@mui/material/CircularProgress";
import { formatDateInNewfoundland } from "../../../utils/dates/formatDateInNewfoundland";
import formatCartItemDetails from "../../../utils/formatCartItemDetails";
import formatCategoryLabel from "../../../utils/formatCategoryLabel";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import {
  isDelivery,
  normalizeLocation,
  deliveryAddressString,
} from "../../../utils/fulfillment";

dayjs.extend(utc);
dayjs.extend(timezone);

const columns = [
  { id: "orderId", label: "Order ID" },
  { id: "customer", label: "Customer" },
  { id: "orderDate", label: "Order Date" },
  { id: "pickupDate", label: "Pickup/Delivery Date" },
  { id: "location", label: "Location" },
  { id: "total", label: "Total" },
  { id: "firstItem", label: "First Item" },
  { id: "actions", label: "Actions" },
];

const getValueForSorting = (order, id) => {
  const { meta, customer, items, fulfillment, payment } = order;
  switch (id) {
    case "orderId":
      return meta.orderId;
    case "customer":
      return `${customer.firstName} ${customer.lastName}`;
    case "orderDate":
      return dayjs(meta.createdAt).tz("America/St_Johns").valueOf();
    case "pickupDate":
      return dayjs(fulfillment.date).tz("America/St_Johns").valueOf();
    case "location":
      return normalizeLocation(fulfillment);
    case "total":
      return payment.total;
    case "firstItem":
      return items[0]?.name || "";
    default:
      return "";
  }
};

const money = (n) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(
    Number(n || 0)
  );

function OrderItemCell({ item }) {
  const details = formatCartItemDetails(item);
  const categoryLabel = formatCategoryLabel(item);

  const discounts = Array.isArray(item.discountsApplied)
    ? item.discountsApplied
    : [];
  const totalSaved = discounts.reduce((s, d) => s + Number(d.amount || 0), 0);
  const hasDiscount = totalSaved > 0;

  const originalTotal =
    item.originalTotalPrice ??
    (hasDiscount ? Number(item.totalPrice || 0) + totalSaved : null);

  return (
    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
      <Avatar
        variant="rounded"
        src={item.image || item.images?.primary?.url || ""}
        alt={item.displayName || item.name}
        sx={{ width: 120, height: 120 }}
      />
      <Box>
        <Typography
          variant="subtitle2"
          fontWeight="bold"
          color="primary"
          sx={{ fontSize: 15 }}>
          {categoryLabel}
        </Typography>

        <Typography variant="body2">
          Qty: {item.quantity} —{" "}
          {hasDiscount && originalTotal ? (
            <>
              <Box
                component="span"
                sx={{
                  textDecoration: "line-through",
                  color: "text.secondary",
                  mr: 1,
                }}>
                {money(originalTotal)}
              </Box>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {money(item.totalPrice)}
              </Box>
            </>
          ) : (
            <Box component="span">{money(item.totalPrice)}</Box>
          )}
        </Typography>

        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}>
          {details.map(({ label, value }, i) => (
            <Box
              key={i}
              sx={{
                display: "inline-block",
                px: 1.5,
                py: 0.5,
                backgroundColor: "#e0e0e0",
                borderRadius: "16px",
                fontSize: "12px",
                maxWidth: 260,
                wordWrap: "break-word",
                whiteSpace: "normal",
              }}>
              {`${label}: ${value}`}
            </Box>
          ))}
        </Box>

        {discounts.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}>
              Discounts ({money(totalSaved)} saved)
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {discounts.map((d, idx) => {
                const valuePretty =
                  d.valueType === "percentage" ? `${d.value}%` : money(d.value);
                const matched = d.matchedOn || {};
                const matchedBits = [
                  matched.productType && `type: ${matched.productType}`,
                  matched.category && `cat: ${matched.category}`,
                  matched.subcategory && `subcat: ${matched.subcategory}`,
                ]
                  .filter(Boolean)
                  .join(" • ");

                const tooltip = [
                  `${d.type} · ${valuePretty}`,
                  matchedBits,
                  `priority ${d.priority}${d.exclusive ? " · exclusive" : ""}`,
                ]
                  .filter(Boolean)
                  .join("\n");

                return (
                  <Tooltip key={idx} title={tooltip}>
                    <Chip
                      size="small"
                      icon={<LocalOfferIcon sx={{ fontSize: 16 }} />}
                      label={`${d.name} `}
                      variant="outlined"
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function BookingTableRow({ order, onDelete, onSendConfirmation }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sendingOrderId, setSendingOrderId] = useState(null);

  const latestEdit =
    Array.isArray(order.edits) && order.edits.length > 0
      ? order.edits[order.edits.length - 1].orderSnapshot
      : null;

  const displayOrder = latestEdit ? { ...order, ...latestEdit } : order;
  const { meta, customer, items, fulfillment, payment, internal } =
    displayOrder;

  const firstItem = items[0];
  const orderDate = formatDateInNewfoundland(meta.createdAt);
  const pickupDate = formatDateInNewfoundland(fulfillment.date);
  const orderTime = formatDateInNewfoundland(meta.createdAt, "h:mm A");

  const delivery = isDelivery(fulfillment);
  const storeLocation = normalizeLocation(fulfillment);
  const addressStr = deliveryAddressString(fulfillment);

  return (
    <>
      <TableRow hover onClick={() => setOpen(!open)} sx={{ cursor: "pointer" }}>
        <TableCell>{meta.orderId}</TableCell>
        <TableCell>
          <Typography fontWeight={600}>
            {customer.firstName} {customer.lastName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {customer.email}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {customer.phone}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography>{orderDate}</Typography>
          <Typography variant="caption" color="text.secondary">
            {orderTime}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography>{pickupDate}</Typography>
          <Typography variant="caption" color="text.secondary">
            {fulfillment.time}
          </Typography>
        </TableCell>
        <TableCell>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexWrap: "wrap",
            }}>
            <Typography>{storeLocation}</Typography>
            {delivery && (
              <Chip
                size="small"
                color="info"
                variant="outlined"
                icon={<LocalShippingIcon sx={{ fontSize: 16 }} />}
                label="Delivery"
              />
            )}
          </Box>
        </TableCell>
        <TableCell>${payment.total.toFixed(2)}</TableCell>
        <TableCell>
          <OrderItemCell item={firstItem} />
        </TableCell>
        <TableCell align="center">
          {meta.status === "in-progress" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
              }}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/orders/${order._id}/edit`);
                }}
                size="small"
                aria-label="edit">
                <EditIcon />
              </IconButton>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.(order._id);
                }}
                size="small"
                aria-label="delete">
                <DeleteIcon />
              </IconButton>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setSendingOrderId(order._id);
                  onSendConfirmation(order._id).finally(() => {
                    setSendingOrderId(null);
                  });
                }}
                size="small"
                aria-label="send confirmation">
                {sendingOrderId === order._id ? (
                  <CircularProgress size={20} />
                ) : (
                  <EmailIcon />
                )}
              </IconButton>
            </Box>
          )}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Full Item List
              </Typography>

              <Grid container spacing={2}>
                {items.map((item, i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <OrderItemCell item={item} />
                    {item.note && (
                      <Box mt={1} ml={1}>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontStyle: "italic" }}>
                          Note: {item.note}
                        </Typography>
                      </Box>
                    )}
                    {Array.isArray(item.referenceImages) &&
                      item.referenceImages.length > 0 && (
                        <Box mt={2}>
                          <ReferenceImagePreviewList
                            images={item.referenceImages}
                          />
                        </Box>
                      )}
                  </Grid>
                ))}
              </Grid>

              {/* Delivery address only for delivery orders */}
              {delivery && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Delivery Address
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: "pre-wrap" }}>
                    {addressStr}
                  </Typography>
                </Box>
              )}

              {internal?.kitchenNotes && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Kitchen Notes
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}>
                    {internal.kitchenNotes}
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const BookingTable = ({ orders, onDelete, onSendConfirmation }) => {
  const [sortBy, setSortBy] = useState("pickupDate");
  const [sortOrder, setSortOrder] = useState("asc");

  const handleSort = (columnId) => {
    if (sortBy === columnId) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(columnId);
      setSortOrder("asc");
    }
  };

  const sortedOrders = [...orders].sort((a, b) => {
    const valA = getValueForSorting(a, sortBy);
    const valB = getValueForSorting(b, sortBy);

    if (valA < valB) return sortOrder === "asc" ? -1 : 1;
    if (valA > valB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, pt: 2, width: "100%", overflowX: "auto" }}>
      <TableContainer sx={{ maxHeight: 2000 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map(({ id, label }) => (
                <TableCell
                  key={id}
                  onClick={() => handleSort(id)}
                  sx={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#f5f5f5",
                    fontWeight: "bold",
                    zIndex: 2,
                    cursor: id !== "actions" ? "pointer" : "default",
                    userSelect: "none",
                    "&:hover .sort-icon": {
                      visibility: id !== "actions" ? "visible" : "hidden",
                    },
                  }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}>
                    <Typography variant="body2" fontWeight="bold">
                      {label}
                    </Typography>
                    {id !== "actions" && (
                      <Box
                        className="sort-icon"
                        component="span"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color:
                            sortBy === id
                              ? "text.primary"
                              : "rgba(0, 0, 0, 0.3)",
                          visibility: sortBy === id ? "visible" : "hidden",
                          transition: "color 0.2s, visibility 0.2s",
                        }}>
                        {sortBy === id ? (
                          sortOrder === "asc" ? (
                            <KeyboardArrowUp fontSize="small" />
                          ) : (
                            <KeyboardArrowDown fontSize="small" />
                          )
                        ) : (
                          <KeyboardArrowUp fontSize="small" />
                        )}
                      </Box>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedOrders.map((order, index) => (
              <BookingTableRow
                key={index}
                order={order}
                onDelete={onDelete}
                onSendConfirmation={onSendConfirmation}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default BookingTable;
